import React, { useEffect, useState } from 'react';
import { ToggleLeft, ToggleRight, Save } from 'lucide-react';
import { useWorkflowSettings, useUpdateWorkflowSettings, FINE_TYPE_LABELS } from '../hooks/useWorkflowSettings.ts';

/* Two switches that change how the group works day to day. Both are off until an admin turns them on. */
function Switch({ on, onChange, label }) {
  const Icon = on ? ToggleRight : ToggleLeft;
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)}>
      <Icon size={32} className={on ? 'text-green-600' : 'text-gray-400'} />
    </button>
  );
}

export default function WorkflowSettings() {
  const { data, isLoading } = useWorkflowSettings();
  const save = useUpdateWorkflowSettings();
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (data) setForm({ ...data, call_register_fine_rates: { ...data.call_register_fine_rates } });
  }, [data]);

  if (isLoading || !form) return <p className="text-sm text-gray-500">Loading settings...</p>;

  const dirty = JSON.stringify(form) !== JSON.stringify(data);
  const setRate = (key, value) => setForm((f) => ({ ...f, call_register_fine_rates: { ...f.call_register_fine_rates, [key]: value === '' ? 0 : Number(value) } }));

  return (
    <div className="space-y-4" data-testid="workflow-settings">
      <div className="border border-gray-100 rounded-lg p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-medium text-gray-900">Welfare approval</h3>
            <p className="mt-1 text-sm text-gray-600">
              When this is on, a member who has not paid welfare contributions for 3 or more people waits for officials to approve their
              request before they join the needy list. Everyone else is approved straight away.
            </p>
          </div>
          <Switch on={form.welfare_vetting_enabled} onChange={(v) => setForm({ ...form, welfare_vetting_enabled: v })} label="Welfare approval" />
        </div>
        {form.welfare_vetting_enabled && (
          <label className="mt-4 block text-sm text-gray-700">
            Approvals needed from officials
            <input
              type="number" min="1" max="10" value={form.welfare_vetting_approvals}
              onChange={(e) => setForm({ ...form, welfare_vetting_approvals: Math.max(1, Math.min(10, Number(e.target.value) || 1)) })}
              className="mt-1 block w-24 rounded-md border border-gray-300 px-3 py-2"
            />
          </label>
        )}
      </div>

      <div className="border border-gray-100 rounded-lg p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-medium text-gray-900">Meeting call register</h3>
            <p className="mt-1 text-sm text-gray-600">
              Take a roll call at each meeting and charge fines automatically for late comers, disturbance and absence. Fines are taken
              from the member's project account first.
            </p>
          </div>
          <Switch on={form.call_register_enabled} onChange={(v) => setForm({ ...form, call_register_enabled: v })} label="Call register" />
        </div>
        {form.call_register_enabled && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700">Fine for each case (KES)</p>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.keys(FINE_TYPE_LABELS).map((key) => (
                <label key={key} className="text-sm text-gray-700">
                  {FINE_TYPE_LABELS[key]}
                  <input
                    type="number" min="0" value={form.call_register_fine_rates[key] ?? 0}
                    onChange={(e) => setRate(key, e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  />
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button" disabled={!dirty || save.isPending} onClick={() => save.mutate(form)}
          className="flex items-center px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
        >
          <Save size={18} className="mr-2" />
          {save.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
